/**
 * `SCR-QUEUE` (9 states) and `SCR-PRODQUEUE` (2). FLOWS_SPEC §10.2.
 * OWNER: web-frontend-engineer.
 *
 * The production queue is `BackOfficeTable` FILTERED — the same data and the
 * same components, never a second table.
 *
 * `needs` and the sort are computed SERVER-SIDE. The UI does not hold its own
 * copy of the state machine. There are no inline state changes and no bulk
 * actions: a transition asserts that a person did a thing.
 */

import { useState } from 'react';
import { t, tDynamic } from '@pt/copy';
import type { OrderState } from '@pt/core';
import { api } from '../../lib/api.ts';
import { useResource } from '../../lib/resource.ts';
import type { QueueRow } from '../../lib/contracts.ts';
import { dateInWords, joinList, metres, money } from '@pt/view';
import { AppShell } from '../../app/AppShell.tsx';
import { useNavigation } from '../../app/Router.tsx';
import { BackOfficeTable, type Column } from '../../components/backoffice.tsx';
import {
  Button,
  FactLine,
  RovingRadioGroup,
  SelectionMark,
  Statement,
} from '../../components/primitives.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from '../../components/states.tsx';

const WORKSHOP_STATES: readonly OrderState[] = [
  'confirmed',
  'cut',
  'constructed',
  'fitting',
  'alterations',
];

/**
 * The order a suit is BUILT in, for laying the stage chips out left to right.
 * This is a display order, not a copy of the machine — it holds no transition
 * and no rule about who may move what — and it is typed against the whole
 * `OrderState` union so a state added to the domain fails to compile here
 * before it can land unsorted at the end of the row. The two states that are
 * not stages of a build, halted and cancelled, come last.
 */
const BUILD_ORDER: Readonly<Record<OrderState, number>> = {
  placed: 0,
  confirmed: 1,
  cut: 2,
  constructed: 3,
  fitting: 4,
  alterations: 5,
  finished: 6,
  delivered: 7,
  halted: 8,
  cancelled: 9,
};

/** The distinct states present, in build order. */
export function inBuildOrder(states: readonly OrderState[]): readonly OrderState[] {
  return [...new Set(states)].sort((a, b) => BUILD_ORDER[a] - BUILD_ORDER[b]);
}

/**
 * The queue's two filters. 5 September 2026 (finding C6): the stage options
 * stood in a column — ten of them, 501 px tall, the table 517 px down the page
 * (measured at 1280×800) — in the order the rows happened to arrive; and the
 * "Needs action only" switch had `aria-checked` and NOTHING ON SCREEN to show
 * it, because it rendered neither the mark nor the `__label` span the selected
 * rule bites on (the same half-fix `RovingRadioGroup` had, found the same way).
 * Now: one row of chips in build order, and a switch with the same mark every
 * other option carries.
 */
export function QueueFilters({
  stages,
  stage,
  onStage,
  needsOnly,
  onNeedsOnly,
}: {
  readonly stages: readonly OrderState[];
  readonly stage: string;
  readonly onStage: (value: string) => void;
  readonly needsOnly: boolean;
  readonly onNeedsOnly: () => void;
}) {
  return (
    <div className="pt-filters">
      <fieldset className="pt-filter">
        <legend className="pt-filter__legend">{t('queue.filter.stage')}</legend>
        <RovingRadioGroup
          ariaLabel={t('queue.filter.stage')}
          values={inBuildOrder(stages)}
          chosen={stage}
          labelOf={(value) => tDynamic(`atelier.stage.${value}`)}
          onChoose={onStage}
          toggle
          rowClassName="pt-filter__values pt-filter__values--row"
        />
      </fieldset>
      <fieldset className="pt-filter">
        <legend className="pt-filter__legend">{t('queue.filter.needsAction')}</legend>
        <div className="pt-filter__values pt-filter__values--row">
          <button
            type="button"
            role="switch"
            aria-checked={needsOnly}
            className="pt-option"
            onClick={onNeedsOnly}
          >
            <SelectionMark checked={needsOnly} />
            <span className="pt-option__body">
              <span className="pt-option__label">{t('queue.filter.needsAction')}</span>
            </span>
          </button>
        </div>
      </fieldset>
    </div>
  );
}

export function QueueScreen({ production = false }: { production?: boolean }) {
  const { navigate } = useNavigation();
  const [needsOnly, setNeedsOnly] = useState(false);
  const [stage, setStage] = useState<string>('');
  const queue = useResource(() => api.atelierOrders({}), []);

  /* The server's own sentence where the figures were, or null when they are
     there. It decides both the column and the line above the table. */
  const withheld = queue.data?.commercialsWithheld ?? null;
  const all = queue.data?.orders ?? [];
  const rows = all
    .filter((row) => (production ? WORKSHOP_STATES.includes(row.state) : true))
    .filter((row) => (needsOnly ? row.needs !== 'none' : true))
    .filter((row) => (stage === '' ? true : row.state === stage));

  const columns: readonly Column<QueueRow>[] = [
    {
      id: 'order',
      headingKey: 'queue.column.order',
      render: (row) => (
        <>
          <span className="pt-table__ref">{row.orderRef}</span>
          <span className="pt-table__sub">{row.commissionTypeLabel}</span>
        </>
      ),
    },
    {
      id: 'client',
      headingKey: 'queue.column.client',
      render: (row) => (
        <>
          {row.clientName}
          <span className="pt-table__sub">{row.clientRef}</span>
        </>
      ),
    },
    { id: 'placed', headingKey: 'queue.column.placed', render: (row) => dateInWords(row.placedAt) },
    {
      id: 'cloth',
      headingKey: 'queue.column.cloth',
      render: (row) => (
        <>
          {row.clothName}
          <span className="pt-table__sub">{row.clothId}</span>
        </>
      ),
    },
    {
      id: 'needed',
      headingKey: 'queue.column.clothNeeded',
      numeric: true,
      render: (row) => metres(row.clothNeededM),
    },
    /* THE COLUMN IS ABSENT, NOT EMPTY, for a price-blind principal.
       `middleware/price-blind.ts` removes `priceMinor` and `currency` from
       every row rather than blanking them, and this table believed the type
       that said they were always there: `money(undefined, undefined)` threw
       "Currency code is required with currency style" and took the entire
       workshop queue to the error boundary. A column of dashes would also be
       wrong — a heading called "Value" over nothing invites somebody to go
       looking. The server's own sentence goes above the table instead. */
    ...(withheld === null
      ? [
          {
            id: 'value',
            headingKey: 'queue.column.value',
            numeric: true,
            /* Empty rather than a stand-in figure if the server ever sends a
               row without them: nothing is honest, `$0.00` is not. */
            render: (row: QueueRow) =>
              row.priceMinor === undefined || row.currency === undefined
                ? ''
                : money(row.priceMinor, row.currency),
          } as Column<QueueRow>,
        ]
      : []),
    {
      id: 'stage',
      headingKey: 'queue.column.stage',
      // Text, ALWAYS. Never colour alone.
      render: (row) => tDynamic(`atelier.stage.${row.state}`),
    },
    {
      id: 'promised',
      headingKey: 'queue.column.promised',
      // ST-QUEUE-NODATE — the ONE place a dash is correct: a staff column of a
      // nullable field, not the client's order page.
      render: (row) =>
        row.promisedWindow === null
          ? t('queue.nodate')
          : `${dateInWords(row.promisedWindow.earliest)} – ${dateInWords(row.promisedWindow.latest)}`,
    },
    {
      id: 'needs',
      headingKey: 'queue.column.needs',
      render: (row) =>
        row.needs === 'halted' ? (
          <span className="pt-table__alert">
            {t('queue.needs.halted')}
            <span className="pt-table__sub">
              {t('queue.halted.days', { daysHalted: row.daysHalted ?? 0 })}
            </span>
          </span>
        ) : (
          // The server sends the CODE and its catalogue key. Nothing here maps
          // a label back to a code, and nothing builds a key from a code.
          tDynamic(row.needsCopyKey)
        ),
    },
  ];

  return (
    <AppShell
      title={production ? t('prodqueue.heading') : t('queue.heading')}
      banner={queue.phase === 'offline' ? <OfflineStatement /> : null}
    >
      {queue.phase === 'skeleton' || queue.phase === 'idle' ? (
        <LoadingRegion rows={10} variant="table" slow={queue.slow} label={t('queue.heading')} />
      ) : null}
      {queue.phase === 'error' ? <FetchError whatKey="queue.error.what" onRetry={queue.reload} /> : null}

      {queue.phase !== 'ready' ? null : all.length === 0 ? (
        <Statement>{production ? t('prodqueue.empty') : t('queue.empty')}</Statement>
      ) : (
        <>
          {/* ST-QUEUE-ESTIMATE — the Cloth-needed column's standing statement. */}
          <FactLine>{t('queue.estimate.statement')}</FactLine>

          {production ? null : (
            <QueueFilters
              stages={all.map((row) => row.state)}
              stage={stage}
              onStage={setStage}
              needsOnly={needsOnly}
              onNeedsOnly={() => setNeedsOnly((value) => !value)}
            />
          )}

          {/* Said once, above the table, in the server's own words — not a
              column of blanks a person has to interpret. */}
          {withheld === null ? null : <FactLine>{withheld}</FactLine>}

          {rows.length === 0 ? (
            <div>
              {/* ST-QUEUE-FILTERED-EMPTY — names the filters that are on and
                  clears them in one action. Never implies there is no work. */}
              <Statement>
                {t('queue.filteredEmpty', {
                  activeFiltersList: joinList(
                    [
                      stage === '' ? '' : tDynamic(`atelier.stage.${stage}`),
                      needsOnly ? t('queue.filter.needsAction') : '',
                    ].filter((value) => value !== ''),
                  ),
                })}
              </Statement>
              <div className="pt-actions">
                <Button
                  onClick={() => {
                    setStage('');
                    setNeedsOnly(false);
                  }}
                >
                  {t('global.action.clearFilters')}
                </Button>
              </div>
            </div>
          ) : (
            <BackOfficeTable
              caption={production ? t('prodqueue.heading') : t('queue.heading')}
              columns={columns}
              rows={rows}
              rowKey={(row) => row.orderRef}
              rowHref={(row) => `/atelier/orders/${row.orderRef}`}
              onOpenRow={(row) => navigate(`/atelier/orders/${row.orderRef}`)}
            />
          )}
        </>
      )}
    </AppShell>
  );
}
